import React from "react";
import { Navigate, useParams } from "react-router-dom";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import { useAppSelector } from "src/store/store";
import AuditLayout from "./AuditLayout";
import AuditWorkspace from "../workspace/AuditWorkspace";
import { useAuditAccess } from "./useAuditAccess";

/** Wrapper that passes the route taskId into AuditLayout so the sidebar highlights Workspace. */
export const WorkspaceWithLayout: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  return (
    <AuditLayout activeTaskId={taskId}>
      <AuditWorkspace />
    </AuditLayout>
  );
};

/** Wrapper for non-workspace audit pages — reads lastActiveTaskId from Redux so the
 *  Workspace nav item stays clickable after navigating away from the workspace. */
export const AuditPageLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const lastActiveTaskId = useAppSelector((state) => state.auditWorkspaceSlice.lastActiveTaskId);
  return <AuditLayout activeTaskId={lastActiveTaskId ?? undefined}>{children}</AuditLayout>;
};

/** Guard for the Configuration module. Admins and Program Owners pass; Auditors
 *  (and anyone without an audit role) are sent back to the Audit Queue. While the
 *  role lookup is in flight it shows a loader rather than bouncing a deep link.
 *  ``adminOnly`` restricts a page (e.g. creating a use case) to Admins. */
export const ConfigRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({
  children,
  adminOnly,
}) => {
  const { resolving, canManageConfig, isAdmin } = useAuditAccess();
  if (resolving) {
    return (
      <AuditPageLayout>
        <Row width="fill" alignmentHorizontal="center">
          <Column height="60vh" alignmentVertical="center">
            <Loader />
          </Column>
        </Row>
      </AuditPageLayout>
    );
  }
  if (!canManageConfig || (adminOnly && !isAdmin)) {
    return <Navigate to="/vlmAudit/my-tasks" replace />;
  }
  return <AuditPageLayout>{children}</AuditPageLayout>;
};
